// ============================================================================
// FarmerAuthContext — logged-in farmer's token + profile (Task #10).
// ============================================================================
// Parallel to AuthContext but for the farmer-facing flows. Two separate
// stores (different JWT aud claim) means a logged-in admin can ALSO have a
// farmer session in the same browser without conflict — e.g. for testing.
//
// Storage key 'uif_farmer_auth' (different from 'uif_auth' for admins).
// localStorage so the farmer stays signed in across tabs and reloads.
// ============================================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import type { Farmer } from './api';

type FarmerLite = Pick<Farmer, 'farmer_id' | 'email' | 'full_name'>;

interface FarmerAuthState {
  token: string | null;
  farmer: FarmerLite | null;
}

interface FarmerAuthContextValue extends FarmerAuthState {
  login: (token: string, farmer: FarmerLite) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

const STORAGE_KEY = 'uif_farmer_auth';

function readStored(): FarmerAuthState {
  if (typeof window === 'undefined') return { token: null, farmer: null };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.token && parsed?.farmer?.farmer_id) {
        return parsed as FarmerAuthState;
      }
    }
  } catch {
    /* ignore */
  }
  return { token: null, farmer: null };
}

const FarmerAuthContext = createContext<FarmerAuthContextValue | undefined>(undefined);

export function FarmerAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<FarmerAuthState>(readStored);

  const login = useCallback((token: string, farmer: FarmerLite) => {
    const next: FarmerAuthState = { token, farmer };
    setState(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); }
    catch { /* localStorage disabled — in-memory still works */ }
  }, []);

  const logout = useCallback(() => {
    setState({ token: null, farmer: null });
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }, []);

  // Cross-tab sync — logging out in one tab logs out in all.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setState(readStored());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<FarmerAuthContextValue>(
    () => ({
      token: state.token,
      farmer: state.farmer,
      login,
      logout,
      isAuthenticated: !!state.token && !!state.farmer,
    }),
    [state, login, logout],
  );

  return <FarmerAuthContext.Provider value={value}>{children}</FarmerAuthContext.Provider>;
}

export function useFarmerAuth(): FarmerAuthContextValue {
  const ctx = useContext(FarmerAuthContext);
  if (!ctx) {
    throw new Error('useFarmerAuth must be used within <FarmerAuthProvider>');
  }
  return ctx;
}
